import { useCallback, useEffect, useRef, useState } from "react";
import { isExistingMember, isNewMember } from "../helpers";
import type { EmailVerificationStatus } from "../types";
import { useHandleSuccessAuthentication } from "./useHandleSuccessAuthentication";

interface UseEmailVerificationOTPStateProps {
  error?: string | null;
  isSubmitting?: boolean;
  onOTPSubmit?: (otp: string) => void;
  onResendCode?: () => void;
  onShowClaimForm?: () => void;
  status: EmailVerificationStatus | null;
}

export const useEmailVerificationOTPState = ({
  status,
  isSubmitting = false,
  error = null,
  onOTPSubmit,
  onResendCode,
  onShowClaimForm,
}: UseEmailVerificationOTPStateProps) => {
  const [hasShownSuccess, setHasShownSuccess] = useState(false);
  const { reloadPage } = useHandleSuccessAuthentication();
  const wasSubmittingRef = useRef(false);

  const handleOTPComplete = useCallback(
    (otp: string) => {
      onOTPSubmit?.(otp);
    },
    [onOTPSubmit],
  );

  const handleResendClick = useCallback(() => {
    onResendCode?.();
  }, [onResendCode]);

  useEffect(() => {
    // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible
    if (isSubmitting) {
      wasSubmittingRef.current = true;
      setHasShownSuccess(false);
    } else if (wasSubmittingRef.current && !isSubmitting) {
      wasSubmittingRef.current = false;

      if (!error) {
        setHasShownSuccess(true);

        if (isExistingMember(status) || isNewMember(status)) {
          reloadPage();
        } else {
          onShowClaimForm?.();
          setHasShownSuccess(false);
        }
      }
    }
  }, [isSubmitting, error, status, onShowClaimForm]);

  useEffect(() => {
    if (error) {
      setHasShownSuccess(false);
    }
  }, [error]);

  const shouldShowSuccess = hasShownSuccess && !isSubmitting && !error;
  const shouldShowFooter = !(isSubmitting || shouldShowSuccess);

  return {
    isSuccessShown: shouldShowSuccess,
    hasError: !!error,
    shouldShowSuccess,
    shouldShowFooter,
    handleOTPComplete,
    handleResendClick,
  };
};
